import { api } from './index'

export const POS_PROVIDERS = [
  'toast',
  'square',
  'qu',
  'ncr',
  'brink',
  'genius',
] as const
export type PosProvider = typeof POS_PROVIDERS[number]

export type ChannelValue = 'instore' | 'digital' | 'third_party'

export type ChannelSource = 'vendor_api' | 'pos_observed' | 'manual'

export type ChannelResponse = {
  provider: PosProvider
  provider_key: string
  provider_label: string | null
  value: ChannelValue | null
  source: ChannelSource | null
  disabled: boolean
  needs_review: boolean
}

export type ChannelMappingsResponse = {
  merchant_id: number
  mappings: ChannelResponse[]
}

export type RevenueSourceResponse = {
  channel: ChannelResponse
  no_channel: boolean
  subtotal_cents: number
  total_cents: number
  order_count: number
  location_id: number
}

export type RevenueSourcesResponse = {
  merchant_id: number
  location_id: number | null
  window_start: string
  window_end: string
  sources: RevenueSourceResponse[]
}

export type AssignChannelParams = {
  provider: PosProvider
  providerKey: string
  value: ChannelValue
}

export type BulkAssignChannelsParams = {
  rows: AssignChannelParams[]
}

export const captureRateApi = api.injectEndpoints({
  endpoints: build => ({
    getCaptureRateChannelMappings: build.query<ChannelMappingsResponse, void>({
      query: () => 'capture_rate/channel_mappings',
      providesTags: ['CaptureRateChannelMappings'],
    }),
    getCaptureRateRevenueSources: build.query<
      RevenueSourcesResponse,
      { locationId?: number }
    >({
      query: ({ locationId }) => ({
        url: 'capture_rate/revenue_sources',
        params: {
          ...(locationId ? { location_id: locationId } : {}),
        },
      }),
      providesTags: ['CaptureRateRevenueSources'],
    }),
    assignCaptureRateChannel: build.mutation<
      ChannelResponse,
      AssignChannelParams
    >({
      query: ({ provider, providerKey, value }) => ({
        url: 'capture_rate/channel_mappings',
        method: 'PATCH',
        body: { provider, provider_key: providerKey, value },
      }),
      // ChannelMappingEntity declares `root 'mappings', 'mapping'`, so the
      // response body is `{ mapping: {...} }`, not a bare ChannelResponse.
      transformResponse: (response: { mapping: ChannelResponse }) =>
        response.mapping,
      // A failed assignment changed nothing server-side -- refetching would
      // only unmount the table out from under the error it just set.
      invalidatesTags: (_result, error) =>
        error
          ? []
          : ['CaptureRateChannelMappings', 'CaptureRateRevenueSources'],
    }),
    bulkAssignCaptureRateChannels: build.mutation<
      ChannelMappingsResponse,
      BulkAssignChannelsParams
    >({
      query: ({ rows }) => ({
        url: 'capture_rate/channel_mappings/bulk_assign',
        method: 'POST',
        body: {
          rows: rows.map(({ provider, providerKey, value }) => ({
            provider,
            provider_key: providerKey,
            value,
          })),
        },
      }),
      // The backend wraps this batch in one transaction (all-or-nothing), so
      // a failed bulk assign is equally a no-op server-side -- same reasoning
      // as assignCaptureRateChannel above.
      invalidatesTags: (_result, error) =>
        error
          ? []
          : ['CaptureRateChannelMappings', 'CaptureRateRevenueSources'],
    }),
  }),
})

export const {
  useGetCaptureRateChannelMappingsQuery,
  useGetCaptureRateRevenueSourcesQuery,
  useAssignCaptureRateChannelMutation,
  useBulkAssignCaptureRateChannelsMutation,
} = captureRateApi
